import { useCallback, useEffect, useMemo, useState } from "react";
import { fr } from "date-fns/locale";
import { format } from "date-fns";
import { Lock, Unlock } from "lucide-react";
import axios, { API, authed } from "@/lib/api";
import { useAdminAction } from "@/lib/adminApi";
import { Calendar } from "@/components/ui/calendar";
import BookingCard from "@/components/admin/BookingCard";

const toIso = (d) => format(d, "yyyy-MM-dd");
const toDate = (s) => new Date(`${s}T00:00:00`);
const LEGEND = [["pending", "En attente", "bg-amber-400/30 text-amber-100"], ["accepted", "Acceptée / paiement", "bg-sky-500/40 text-sky-50"], ["confirmed", "Confirmée", "bg-emerald-500/50 text-emerald-50"], ["blocked", "Bloquée", "bg-slate-600 text-slate-300 line-through"]];

const BlockBox = ({ day, blocked, hasBooking, onBlock, onUnblock }) => {
  const [reason, setReason] = useState("");
  if (blocked) return (
    <div className="border border-white/15 p-4 text-sm flex flex-wrap items-center justify-between gap-3" data-testid="day-blocked-box">
      <span className="flex items-center gap-2"><Lock className="w-4 h-4 text-slate-400" />Date bloquée{blocked.reason && ` — ${blocked.reason}`}</span>
      <button onClick={() => onUnblock(day)} data-testid="unblock-day-btn" className="px-4 py-2 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#050B14] transition-colors duration-300 flex items-center gap-2"><Unlock className="w-4 h-4" />Débloquer</button>
    </div>
  );
  if (hasBooking) return null;
  return (
    <div className="border border-white/15 p-4 text-sm flex flex-wrap items-center gap-3" data-testid="day-block-box">
      <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Motif (entretien, réservation WhatsApp…)" data-testid="block-reason-input"
        className="flex-1 min-w-[200px] bg-[#050B14] border border-white/15 px-3 py-2 outline-none focus:border-[#D4AF37]" />
      <button onClick={() => onBlock(day, reason)} data-testid="block-day-btn" className="px-4 py-2 bg-[#D4AF37] text-[#050B14] hover:bg-[#e6c55a] transition-colors duration-300 flex items-center gap-2"><Lock className="w-4 h-4" />Bloquer cette date</button>
    </div>
  );
};

export default function AdminCalendar({ getToken, pct }) {
  const [bookings, setBookings] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const [day, setDay] = useState(new Date());
  const load = useCallback(async () => {
    const cfg = await authed(getToken);
    const [b, bl] = await Promise.all([axios.get(`${API}/admin/bookings`, cfg), axios.get(`${API}/admin/blocked-dates`, cfg)]);
    setBookings(b.data); setBlocked(bl.data);
  }, [getToken]);
  useEffect(() => { load().catch(() => {}); }, [load]);
  const call = useAdminAction(getToken, load);
  const onAction = (id, action, body = {}) => call("post", `/admin/bookings/${id}/${action}`, body);

  const modifiers = useMemo(() => {
    const by = (sts) => bookings.filter((b) => sts.includes(b.status)).map((b) => toDate(b.date));
    return { pending: by(["pending"]), accepted: by(["approved", "payment_submitted"]), confirmed: by(["confirmed"]), blocked: blocked.map((b) => toDate(b.date)) };
  }, [bookings, blocked]);

  const iso = toIso(day);
  const dayBookings = bookings.filter((b) => b.date === iso && !["declined", "cancelled"].includes(b.status));
  const dayBlocked = blocked.find((b) => b.date === iso);
  const isPast = day < new Date(new Date().toDateString());

  return (
    <div className="grid lg:grid-cols-[auto_1fr] gap-8 items-start" data-testid="admin-calendar">
      <div className="bg-[#0A1325] border border-white/10 p-3">
        <Calendar mode="single" locale={fr} selected={day} onSelect={(d) => d && setDay(d)} modifiers={modifiers}
          modifiersClassNames={Object.fromEntries(LEGEND.map(([k, , c]) => [k, c]))}
          classNames={{ day_selected: "!ring-2 !ring-[#D4AF37] !ring-offset-0", head_cell: "text-slate-500 w-11 font-normal text-[0.8rem]", cell: "h-11 w-11 text-center text-sm p-0 relative", day: "h-11 w-11 p-0 font-normal hover:bg-white/10 transition-colors" }} />
        <div className="grid grid-cols-2 gap-2 p-3 text-xs" data-testid="admin-calendar-legend">
          {LEGEND.map(([k, label, c]) => <span key={k} className="flex items-center gap-2"><span className={`w-3 h-3 ${c}`} />{label}</span>)}
        </div>
      </div>
      <div className="space-y-4">
        <h3 className="font-display text-3xl first-letter:uppercase" data-testid="selected-day-title">{format(day, "EEEE d MMMM yyyy", { locale: fr })}</h3>
        {!isPast && <BlockBox day={iso} blocked={dayBlocked} hasBooking={dayBookings.some((b) => b.status !== "pending")}
          onBlock={(d, reason) => call("post", "/admin/blocked-dates", { date: d, reason })}
          onUnblock={(d) => call("delete", `/admin/blocked-dates/${d}`)} />}
        {dayBookings.length === 0 && <p className="text-slate-500 text-sm" data-testid="day-empty">Aucune réservation ce jour-là.</p>}
        {dayBookings.map((b) => <BookingCard key={b.id} b={b} defaultPct={pct} onAction={onAction} />)}
      </div>
    </div>
  );
}
